// src/pages/MatchingPersoneel.js
// vMATCH-PER-012
//
// StaffIQ HRM - Matching Personeel
// Eerste fase:
// - Links/boven: zoeken + knoppen
// - Midden: resultaten-grid met sortering en kolomfilters
// - Rechts: selectiecriteria
// - Popup: volledige personeelskaart bekijken/wijzigen/opslaan/printen/document opslaan

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";

const VERSION = "vMP-012";

const buildUrl = (u) =>
  u.startsWith("http") ? u : `${u.startsWith("/") ? u : `/${u}`}`;

function norm(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[，、]/g, ",")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[;:!?]/g, "")
    .replace(/\s+/g, " ");
}

function safeTrim(s) {
  return String(s || "").trim();
}

function uniqSorted(arr) {
  const set = new Set((arr || []).map((x) => String(x).trim()).filter(Boolean));
  return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
}

function isoDate(s) {
  const t = String(s || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : "";
}

function formatDateNL(iso) {
  const s = String(iso || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
}

function parseDecimal(v) {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(val) {
  const n = Number(val || 0);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseActiveValue(it) {
  const raw = it?.active ?? it?.is_active ?? it?.isActive ?? it?.actief;
  if (raw === undefined || raw === null || raw === "") return null;
  if (raw === true) return true;
  if (raw === false) return false;
  const s = String(raw).toLowerCase().trim();
  if (raw === 1 || s === "1" || s === "true" || s === "yes" || s === "y" || s === "j" || s === "ja" || s === "aan" || s === "on") return true;
  if (raw === 0 || s === "0" || s === "false" || s === "no" || s === "n" || s === "nee" || s === "uit" || s === "off") return false;
  return !!raw;
}

function isEmptyWord(t) {
  const x = norm(t);
  return x === "geen" || x === "blanco" || x === "leeg" || x === "null";
}

function textIncludes(source, needle) {
  const n = norm(needle);
  if (!n) return true;
  return norm(source).includes(n);
}

function boolMatches(value, wanted) {
  const w = norm(wanted);
  if (!w) return true;
  const v = value === true ? "j ja true 1" : "n nee false 0";
  return v.includes(w);
}

function idPresentAndValid(person) {
  const hasDoc = !!safeTrim(person.id_type || person.id_number || "");
  if (!hasDoc) return false;

  const exp = isoDate(person.id_expiry_date);
  if (!exp) return true;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(`${exp}T00:00:00`);
  return expiry >= today;
}

async function tryFetchJson(urls, options = {}) {
  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  const tenant =
    sessionStorage.getItem("tenant") ||
    localStorage.getItem("tenant") ||
    "demo";

  let role = "user";
  try {
    role =
      JSON.parse(
        sessionStorage.getItem("user") ||
        localStorage.getItem("user") ||
        "{}"
      )?.role || "user";
  } catch {}

  let lastErr = null;

  for (const url of urls) {
    try {
      const headers = {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : undefined,
        "x-tenant": tenant,
        "x-role": role,
      };

      Object.keys(headers).forEach(
        (k) => headers[k] === undefined && delete headers[k]
      );

      const res = await fetch(buildUrl(url), {
        ...options,
        headers,
      });

      if (!res.ok) {
        const raw = await res.text();
        let msg = `${url} (${res.status})`;

        try {
          const data = raw ? JSON.parse(raw) : null;
          msg = data?.error || data?.message || raw || msg;
        } catch {
          msg = raw || msg;
        }

        throw new Error(msg);
      }

      if (res.status === 204) return null;
      const text = await res.text();
      if (!text) return null;

      try {
        return JSON.parse(text);
      } catch {
        return text;
      }
    } catch (err) {
      lastErr = err;
    }
  }

  throw lastErr;
}

function normalizePhoneForCommunication(raw, defaultCountryCode = "+31") {
  let s = String(raw || "").trim();

  if (!s || s === "-") return "";

  s = s
    .replace(/[^\d+]/g, "")
    .replace(/^00/, "+");

  if (!s) return "";

  if (s.startsWith("+")) return s;

  if (s.startsWith("0")) {
    return `${defaultCountryCode}${s.slice(1)}`;
  }

  if (s.startsWith("31")) {
    return `+${s}`;
  }

  return `${defaultCountryCode}${s}`;
}

async function loadCommunicationSettingsSafe() {
  try {
    const data = await tryFetchJson(["/api/communication-settings"]);

    return {
      email_enabled: data?.email_enabled !== false,
      whatsapp_enabled: data?.whatsapp_enabled !== false,
      sms_enabled: data?.sms_enabled === true,
      default_country_code: data?.default_country_code || "+31",
      default_sms_text: data?.default_sms_text || "",
      default_whatsapp_text: data?.default_whatsapp_text || "",
    };
  } catch {
    return {
      email_enabled: true,
      whatsapp_enabled: true,
      sms_enabled: false,
      default_country_code: "+31",
      default_sms_text: "",
      default_whatsapp_text: "",
    };
  }
}

function stopGridClick(e) {
  try {
    e.preventDefault();
    e.stopPropagation();
  } catch {}
}

function askCommunicationChoice(raw) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");

    overlay.style.position = "fixed";
    overlay.style.left = "0";
    overlay.style.top = "0";
    overlay.style.right = "0";
    overlay.style.bottom = "0";
    overlay.style.background = "rgba(0,0,0,0.35)";
    overlay.style.zIndex = "999999";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.padding = "18px";
    overlay.style.boxSizing = "border-box";

    const box = document.createElement("div");

    box.style.width = "min(520px, 100%)";
    box.style.background = "#fff";
    box.style.borderRadius = "12px";
    box.style.boxShadow = "0 18px 50px rgba(0,0,0,0.35)";
    box.style.padding = "18px";
    box.style.boxSizing = "border-box";
    box.style.fontFamily = "Arial, Helvetica, sans-serif";
    box.style.color = "#111";

    const title = document.createElement("div");
    title.style.fontWeight = "700";
    title.style.fontSize = "18px";
    title.style.marginBottom = "12px";
    title.textContent = `Wat wil je doen met ${raw}?`;

    const help = document.createElement("div");
    help.style.fontSize = "16px";
    help.style.lineHeight = "1.8";
    help.style.marginBottom = "12px";
    help.innerHTML = "1 = Bellen<br/>2 = SMS<br/>3 = WhatsApp";

    const input = document.createElement("input");
    input.type = "text";
    input.value = "3";
    input.inputMode = "numeric";
    input.autocomplete = "off";
    input.style.width = "100%";
    input.style.fontSize = "20px";
    input.style.padding = "10px 12px";
    input.style.border = "2px solid #1f4e79";
    input.style.borderRadius = "8px";
    input.style.boxSizing = "border-box";
    input.style.marginBottom = "14px";

    const buttons = document.createElement("div");
    buttons.style.display = "flex";
    buttons.style.gap = "10px";
    buttons.style.justifyContent = "flex-end";

    const okButton = document.createElement("button");
    okButton.type = "button";
    okButton.textContent = "OK";
    okButton.style.padding = "10px 18px";
    okButton.style.border = "none";
    okButton.style.borderRadius = "8px";
    okButton.style.background = "#1f4e79";
    okButton.style.color = "#fff";
    okButton.style.fontSize = "16px";
    okButton.style.cursor = "pointer";

    const cancelButton = document.createElement("button");
    cancelButton.type = "button";
    cancelButton.textContent = "Annuleren";
    cancelButton.style.padding = "10px 18px";
    cancelButton.style.border = "1px solid #d0d0d0";
    cancelButton.style.borderRadius = "8px";
    cancelButton.style.background = "#fff";
    cancelButton.style.color = "#111";
    cancelButton.style.fontSize = "16px";
    cancelButton.style.cursor = "pointer";

    let done = false;

    function cleanup(value) {
      if (done) return;
      done = true;

      try {
        document.body.removeChild(overlay);
      } catch {}

      resolve(value);
    }

    okButton.onclick = () => cleanup(input.value);
    cancelButton.onclick = () => cleanup(null);

    input.onkeydown = (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        cleanup(input.value);
      }

      if (ev.key === "Escape") {
        ev.preventDefault();
        cleanup(null);
      }
    };

    overlay.onclick = (ev) => {
      if (ev.target === overlay) {
        cleanup(null);
      }
    };

    buttons.appendChild(cancelButton);
    buttons.appendChild(okButton);

    box.appendChild(title);
    box.appendChild(help);
    box.appendChild(input);
    box.appendChild(buttons);

    overlay.appendChild(box);
    document.body.appendChild(overlay);

    setTimeout(() => {
      try {
        input.focus();
        input.select();
      } catch {}
    }, 80);
  });
}

async function handleEmailGridClick(e, email) {
  stopGridClick(e);

  const mail = String(email || "").trim();

  if (!mail || mail === "-") return;

  const settings = await loadCommunicationSettingsSafe();

  if (settings.email_enabled === false) {
    alert("E-mail is niet actief in Communicatie-instellingen.");
    return;
  }

  window.location.href = `mailto:${encodeURIComponent(mail)}`;
}

function handlePhoneGridClick(e, phone) {
  stopGridClick(e);

  const tel = String(phone || "").trim();

  if (!tel || tel === "-") return;

  window.location.href = `tel:${tel}`;
}

async function handleMobileGridClick(e, mobile) {
  stopGridClick(e);

  const raw = String(mobile || "").trim();

  if (!raw || raw === "-") return;

  const settings = await loadCommunicationSettingsSafe();
  const normalized = normalizePhoneForCommunication(raw, settings.default_country_code || "+31");

  if (!normalized) return;

  const choice = await askCommunicationChoice(raw);

  const c = String(choice || "").trim().toLowerCase();

  if (!c) return;

  if (c === "1" || c === "bellen" || c === "bel") {
    window.location.href = `tel:${raw}`;
    return;
  }

  if (c === "2" || c === "sms") {
    if (settings.sms_enabled !== true) {
      alert("SMS is niet actief in Communicatie-instellingen.");
      return;
    }

    const text = window.prompt(
      `SMS naar ${raw}`,
      settings.default_sms_text || ""
    );

    if (text === null) return;

    const message = String(text || "").trim();

    if (!message) {
      alert("SMS tekst ontbreekt.");
      return;
    }

    try {
      await tryFetchJson(["/api/communications/sms"], {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: raw,
          text: message,
        }),
      });

      alert(`SMS verstuurd naar ${raw}`);
    } catch (err) {
      alert(err?.message || "SMS verzenden mislukt.");
    }

    return;
  }

  if (c === "3" || c === "whatsapp" || c === "app") {
    if (settings.whatsapp_enabled === false) {
      alert("WhatsApp is niet actief in Communicatie-instellingen.");
      return;
    }

    const whatsappNumber = normalized.replace(/[^\d]/g, "");
    const text = String(settings.default_whatsapp_text || "").trim();
    const suffix = text ? `?text=${encodeURIComponent(text)}` : "";

    window.open(`https://wa.me/${whatsappNumber}${suffix}`, "_blank", "noopener,noreferrer");
  }
}

const emptyCriteria = {
  wildcard: "",
  status: "",
  functie: "",
  uurloonMax: "",
  regioBezoek: "",
  woonplaatsBezoek: "",
  rijbewijs: "",
  eigenVervoer: "",
  opmerking: "",
  zzp: "",
  matchingGegevens: "",
  voornaam: "",
  tussenvoegsel: "",
  achternaam: "",
  geslacht: "",
  geboortedatumMode: "",
  geboortedatum: "",
  idDocumenten: "",
};

const emptyForm = {
  id: null,
  first_name: "",
  initials: "",
  name_prefix: "",
  last_name: "",
  gender: "",
  birth_date: "",
  salutation: "",
  attention_to: "",
  search_name: "",
  status: "Actief",
  job_functions: "",
  marital_status: "",
  birth_place: "",
  birth_country: "",
  nationality: "",
  employment_start_date: "",
  employment_end_date: "",
  hourly_wage: "",
  sales_rate: "",
  id_type: "",
  id_number: "",
  id_expiry_date: "",
  bsn: "",
  residence_permit: "",
  driving_license: "",
  own_transport: false,
  transport_description: "",
  visit_street: "",
  visit_house_number: "",
  visit_addition: "",
  visit_postcode: "",
  visit_city: "",
  visit_province: "",
  visit_region: "",
  visit_country: "",
  corr_street: "",
  corr_house_number: "",
  corr_addition: "",
  corr_postcode: "",
  corr_city: "",
  corr_province: "",
  corr_region: "",
  corr_country: "",
  phone: "",
  mobile: "",
  email: "",
  website: "",
  post_digital: true,
  mailing_list: true,
  zzp: false,
  zzp_company_name: "",
  zzp_kvk: "",
  zzp_vat_number: "",
  zzp_vat_rate: "",
  zzp_purchase_rate: "",
  zzp_sales_rate: "",
  own_insurance: false,
  insurance_company: "",
  insurance_name: "",
  insurance_number: "",
  remarks: "",
  profile_photo_path: "",
  active: true,
};

function payloadFromForm(form) {
  return {
    ...form,
    birth_date: form.birth_date || null,
    employment_start_date: form.employment_start_date || null,
    employment_end_date: form.employment_end_date || null,
    id_expiry_date: form.id_expiry_date || null,
    hourly_wage: parseDecimal(form.hourly_wage),
    sales_rate: parseDecimal(form.sales_rate),
    zzp_purchase_rate: parseDecimal(form.zzp_purchase_rate),
    zzp_sales_rate: parseDecimal(form.zzp_sales_rate),
    post_digital: form.post_digital === true,
    mailing_list: form.mailing_list === true,
    zzp: form.zzp === true,
    own_transport: form.own_transport === true,
    own_insurance: form.own_insurance === true,
    active: form.active !== false,
  };
}

export default function MatchingPersoneel() {
  const rawUser =
    sessionStorage.getItem("user") ||
    localStorage.getItem("user") ||
    "";

  let role = "user";
  try {
    role = JSON.parse(rawUser || "{}")?.role || "user";
  } catch {}

  const isReadonlyRole = String(role || "").toLowerCase() === "readonly";

  const [items, setItems] = useState([]);
  const [criteria, setCriteria] = useState(emptyCriteria);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [speechInfo, setSpeechInfo] = useState("");

  const [sort, setSort] = useState({ key: "search", dir: "asc" });
  const [listIndex, setListIndex] = useState(-1);

  const [searchNameFilter, setSearchNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [functionFilter, setFunctionFilter] = useState("");
  const [pcFilter, setPcFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [mobileFilter, setMobileFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [zzpFilter, setZzpFilter] = useState("");
  const [wageFilter, setWageFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("j");

  const [popupOpen, setPopupOpen] = useState(false);
  const [popupSaving, setPopupSaving] = useState(false);
  const [popupError, setPopupError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [matchingRows, setMatchingRows] = useState([]);
  const [documentName, setDocumentName] = useState("");

  const searchRef = useRef(null);
  const rowRefs = useRef([]);
  const popupScrollRef = useRef(null);

  const wildcardRef = useRef(null);
  const statusRef = useRef(null);
  const functieRef = useRef(null);
  const uurloonRef = useRef(null);
  const regioRef = useRef(null);
  const woonplaatsRef = useRef(null);
  const rijbewijsRef = useRef(null);
  const eigenVervoerRef = useRef(null);
  const opmerkingRef = useRef(null);
  const zzpRef = useRef(null);
  const matchingRef = useRef(null);
  const voornaamRef = useRef(null);
  const tussenvoegselRef = useRef(null);
  const achternaamRef = useRef(null);
  const geslachtRef = useRef(null);
  const geboorteModeRef = useRef(null);
  const geboorteDatumRef = useRef(null);

  const criteriaRefs = useMemo(
    () => [
      wildcardRef,
      statusRef,
      functieRef,
      uurloonRef,
      regioRef,
      woonplaatsRef,
      rijbewijsRef,
      eigenVervoerRef,
      opmerkingRef,
      zzpRef,
      matchingRef,
      voornaamRef,
      tussenvoegselRef,
      achternaamRef,
      geslachtRef,
      geboorteModeRef,
      geboorteDatumRef,
    ],
    []
  );

  useEffect(() => {
    loadItems();
  }, []);

  async function loadItems() {
    try {
      setLoading(true);
      setError("");
      const data = await tryFetchJson([
        "/api/personnel-matching/search",
        "/api/personnel?active=",
        "/api/personeel?active=",
      ]);
      setItems(Array.isArray(data) ? data : []);
      setListIndex(-1);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }

  function setCrit(key, value) {
    setCriteria((prev) => ({ ...prev, [key]: value }));
  }

  function resetCriteria() {
    setCriteria(emptyCriteria);
    resetGridFilters();
    setListIndex(-1);
    setTimeout(() => searchRef.current?.focus?.(), 0);
  }

  function resetGridFilters() {
    setSearchNameFilter("");
    setStatusFilter("");
    setFunctionFilter("");
    setPcFilter("");
    setCityFilter("");
    setRegionFilter("");
    setPhoneFilter("");
    setMobileFilter("");
    setEmailFilter("");
    setZzpFilter("");
    setWageFilter("");
    setActiveFilter("j");
  }

  function searchableText(it) {
    return [
      it.id,
      it.search_name,
      it.first_name,
      it.initials,
      it.name_prefix,
      it.last_name,
      it.gender,
      it.birth_date,
      it.status,
      it.job_functions,
      it.marital_status,
      it.birth_place,
      it.birth_country,
      it.nationality,
      it.driving_license,
      it.transport_description,
      it.visit_street,
      it.visit_house_number,
      it.visit_postcode,
      it.visit_city,
      it.visit_region,
      it.corr_street,
      it.corr_house_number,
      it.corr_postcode,
      it.corr_city,
      it.corr_region,
      it.phone,
      it.mobile,
      it.email,
      it.website,
      it.zzp_company_name,
      it.zzp_kvk,
      it.zzp_vat_number,
      it.insurance_company,
      it.insurance_name,
      it.remarks,
      it.matching_text,
    ].join(" ");
  }

const searchNameOptions = useMemo(() => uniqSorted(items.map((x) => x.search_name || "")), [items]);
  const statusOptions = useMemo(() => uniqSorted(items.map((x) => x.status || "")), [items]);
  const functionOptions = useMemo(() => uniqSorted(items.map((x) => x.job_functions || "")), [items]);
  const pcOptions = useMemo(() => uniqSorted(items.map((x) => x.corr_postcode || x.visit_postcode || "")), [items]);
  const cityOptions = useMemo(() => uniqSorted(items.map((x) => x.corr_city || x.visit_city || "")), [items]);
  const regionOptions = useMemo(() => uniqSorted(items.map((x) => x.corr_region || x.visit_region || "")), [items]);
  const phoneOptions = useMemo(() => uniqSorted(items.map((x) => x.phone || "")), [items]);
  const mobileOptions = useMemo(() => uniqSorted(items.map((x) => x.mobile || "")), [items]);
  const emailOptions = useMemo(() => uniqSorted(items.map((x) => x.email || "")), [items]);
  const wageOptions = useMemo(() => uniqSorted(items.map((x) => String(x.hourly_wage ?? ""))), [items]);

  const filteredItems = useMemo(() => {
    const c = criteria;

    const match = (val, filt) => {
      if (!filt) return true;
      return String(val || "") === String(filt);
    };

    return (items || []).filter((it) => {
      if (!textIncludes(searchableText(it), c.wildcard)) return false;
      if (!textIncludes(it.status, c.status)) return false;
      if (!textIncludes(it.job_functions, c.functie)) return false;

      if (safeTrim(c.uurloonMax)) {
        const max = parseDecimal(c.uurloonMax);
        const wage = Number(it.hourly_wage || 0);
        if (Number.isFinite(max) && wage > max) return false;
      }

      if (!textIncludes(it.visit_region || it.corr_region, c.regioBezoek)) return false;
      if (!textIncludes(it.visit_city || it.corr_city, c.woonplaatsBezoek)) return false;
      if (!textIncludes(it.driving_license, c.rijbewijs)) return false;
      if (!boolMatches(it.own_transport === true, c.eigenVervoer)) return false;
      if (!textIncludes(it.remarks, c.opmerking)) return false;
      if (!boolMatches(it.zzp === true, c.zzp)) return false;
      if (!textIncludes(it.matching_text, c.matchingGegevens)) return false;
      if (!textIncludes(it.first_name, c.voornaam)) return false;
      if (!textIncludes(it.name_prefix, c.tussenvoegsel)) return false;
      if (!textIncludes(it.last_name, c.achternaam)) return false;
      if (!textIncludes(it.gender, c.geslacht)) return false;

      const critDate = isoDate(c.geboortedatum);
      if (critDate && c.geboortedatumMode) {
        const bd = isoDate(it.birth_date);
        if (!bd) return false;
        if (c.geboortedatumMode === "jonger" && !(bd > critDate)) return false;
        if (c.geboortedatumMode === "ouder" && !(bd < critDate)) return false;
      }

      if (c.idDocumenten === "aanwezig_geldig" && !idPresentAndValid(it)) return false;
      if (c.idDocumenten === "ontbreekt_of_verlopen" && idPresentAndValid(it)) return false;

      if (!match(it.search_name || "", searchNameFilter)) return false;
      if (!match(it.status || "", statusFilter)) return false;
      if (!match(it.job_functions || "", functionFilter)) return false;
      if (!match(it.corr_postcode || it.visit_postcode || "", pcFilter)) return false;
      if (!match(it.corr_city || it.visit_city || "", cityFilter)) return false;
      if (!match(it.corr_region || it.visit_region || "", regionFilter)) return false;
      if (!match(it.phone || "", phoneFilter)) return false;
      if (!match(it.mobile || "", mobileFilter)) return false;
      if (!match(it.email || "", emailFilter)) return false;

      if (zzpFilter === "j" && it.zzp !== true) return false;
      if (zzpFilter === "n" && it.zzp === true) return false;

      if (!match(String(it.hourly_wage ?? ""), wageFilter)) return false;

      const active = parseActiveValue(it);
      if (activeFilter === "j" && active === false) return false;
      if (activeFilter === "n" && active !== false) return false;

      return true;
    });
  }, [
    items,
    criteria,
    searchNameFilter,
    statusFilter,
    functionFilter,
    pcFilter,
    cityFilter,
    regionFilter,
    phoneFilter,
    mobileFilter,
    emailFilter,
    zzpFilter,
    wageFilter,
    activeFilter,
  ]);

const sortedItems = useMemo(() => {
    const copy = filteredItems.slice();
    const dirMul = sort.dir === "desc" ? -1 : 1;
    const cmp = (a, b) => {
      const aa = norm(a);
      const bb = norm(b);
      if (aa === bb) return 0;
      return aa < bb ? -1 : 1;
    };

    copy.sort((a, b) => {
      if (sort.key === "search") return dirMul * cmp(a.search_name || "", b.search_name || "");
      if (sort.key === "status") return dirMul * cmp(a.status || "", b.status || "");
      if (sort.key === "function") return dirMul * cmp(a.job_functions || "", b.job_functions || "");
      if (sort.key === "pc") return dirMul * cmp(a.corr_postcode || a.visit_postcode || "", b.corr_postcode || b.visit_postcode || "");
      if (sort.key === "city") return dirMul * cmp(a.corr_city || a.visit_city || "", b.corr_city || b.visit_city || "");
      if (sort.key === "region") return dirMul * cmp(a.corr_region || a.visit_region || "", b.corr_region || b.visit_region || "");
      if (sort.key === "phone") return dirMul * cmp(a.phone || "", b.phone || "");
      if (sort.key === "mobile") return dirMul * cmp(a.mobile || "", b.mobile || "");
      if (sort.key === "email") return dirMul * cmp(a.email || "", b.email || "");
      if (sort.key === "zzp") return dirMul * cmp(a.zzp ? "j" : "n", b.zzp ? "j" : "n");
      if (sort.key === "wage") return dirMul * (Number(a.hourly_wage || 0) - Number(b.hourly_wage || 0));
      if (sort.key === "active") return dirMul * cmp(parseActiveValue(a) !== false ? "j" : "n", parseActiveValue(b) !== false ? "j" : "n");
      return dirMul * cmp(a.search_name || "", b.search_name || "");
    });

    return copy;
  }, [filteredItems, sort]);

  const gridCount = Math.min(sortedItems.length, 9999);

  function sortToggle(key) {
    setSort((s) => {
      if (s.key !== key) return { key, dir: "asc" };
      return { key, dir: s.dir === "asc" ? "desc" : "asc" };
    });
  }

  function sortArrow(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

  async function openPerson(row) {
    if (!row?.id) return;

    try {
      setPopupError("");
      const det = await tryFetchJson([
        `/api/personnel/${encodeURIComponent(row.id)}`,
        `/api/personeel/${encodeURIComponent(row.id)}`,
      ]);

      const person = det?.person || det?.personeel || det || row;
      const match = Array.isArray(det?.matching) ? det.matching : [];

      setForm({
        ...emptyForm,
        ...person,
        birth_date: isoDate(person.birth_date),
        employment_start_date: isoDate(person.employment_start_date),
        employment_end_date: isoDate(person.employment_end_date),
        id_expiry_date: isoDate(person.id_expiry_date),
        hourly_wage: person.hourly_wage ?? "",
        sales_rate: person.sales_rate ?? "",
        zzp_purchase_rate: person.zzp_purchase_rate ?? "",
        zzp_sales_rate: person.zzp_sales_rate ?? "",
        post_digital: person.post_digital === true,
        mailing_list: person.mailing_list === true,
        zzp: person.zzp === true,
        own_transport: person.own_transport === true,
        own_insurance: person.own_insurance === true,
        active: parseActiveValue(person) !== false,
      });

      setMatchingRows(match);
      setDocumentName(`Matching ${person.search_name || person.last_name || person.id}`.trim());
      setPopupOpen(true);

      setTimeout(() => {
        try {
          popupScrollRef.current?.scrollTo?.({ top: 0 });
        } catch {}
      }, 0);
    } catch (err) {
      setPopupError(err?.message || String(err));
    }
  }

  async function savePerson() {
    if (isReadonlyRole || !form.id) return;

    if (!safeTrim(form.last_name) && !safeTrim(form.search_name)) {
      setPopupError("Vul minimaal achternaam of zoeknaam in.");
      return;
    }

    try {
      setPopupSaving(true);
      setPopupError("");

      const saved = await tryFetchJson(
        [`/api/personnel/${form.id}`, `/api/personeel/${form.id}`],
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payloadFromForm(form)),
        }
      );

      setForm((prev) => ({ ...prev, ...saved }));
      setItems((prev) => prev.map((x) => String(x.id) === String(form.id) ? { ...x, ...saved } : x));
      setFlash("Personeelskaart opgeslagen ✅");
      setTimeout(() => setFlash(""), 1200);
    } catch (err) {
      setPopupError(err?.message || String(err));
    } finally {
      setPopupSaving(false);
    }
  }

  function escapeHtml(v) {
    return String(v ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function photoUrl(id = form.id) {
    if (!id) return "";
    return buildUrl(`/api/personnel/${encodeURIComponent(id)}/photo`);
  }

  function buildPrintableHtml(printMode = "internal") {
    const isExternal = printMode === "external";
    const fullName = [form.first_name, form.name_prefix, form.last_name].filter(Boolean).join(" ");
    const hasPhoto = !!(form.id && form.profile_photo_path);
    const photoBlock = hasPhoto
      ? `<img src="${escapeHtml(photoUrl(form.id))}" alt="Pasfoto" style="width:90px;height:120px;object-fit:cover;border:1px solid #ccc;border-radius:6px;" />`
      : "";

    const bezoekadres = [
      form.visit_street,
      form.visit_house_number,
      form.visit_addition,
      form.visit_postcode,
      form.visit_city,
      form.visit_province,
      form.visit_region,
      form.visit_country,
    ].filter(Boolean).join(" ");

    const allRows = [
      ["Zoeknaam", form.search_name, "internal"],
      ["Naam", fullName, "both"],
      ["Status", form.status, "internal"],
      ["Functie", form.job_functions, "both"],
      ["Geslacht", form.gender, "both"],
      ["Geboortedatum", formatDateNL(form.birth_date), "both"],
      ["Telefoon", form.phone, "internal"],
      ["Mobiel", form.mobile, "internal"],
      ["E-mail", form.email, "internal"],
      ["Website", form.website, "internal"],
      ["Bezoekadres", bezoekadres, "internal"],
      ["Rijbewijs", form.driving_license, "both"],
      ["Eigen vervoer", form.own_transport ? "Ja" : "Nee", "both"],
      ["ZZP", form.zzp ? "Ja" : "Nee", "both"],
      ["Uurloon", form.hourly_wage ? `€ ${formatMoney(form.hourly_wage)}` : "", "internal"],
      ["ID document", [form.id_type, form.id_number, form.id_expiry_date ? `geldig tot ${formatDateNL(form.id_expiry_date)}` : ""].filter(Boolean).join(" - "), "internal"],
      ["Opmerking", form.remarks, "both"],
    ];

    const rows = isExternal
      ? allRows.filter(([, , visibility]) => visibility === "both")
      : allRows;

    const tr = rows.map(([k, v]) => `
      <tr>
        <td style="width:180px;font-weight:bold;vertical-align:top;border-bottom:1px solid #ddd;padding:6px;">${escapeHtml(k)}</td>
        <td style="border-bottom:1px solid #ddd;padding:6px;white-space:pre-wrap;">${escapeHtml(v || "")}</td>
      </tr>
    `).join("");

    const matching = (matchingRows || []).map((m) => `
      <tr>
        <td style="border-bottom:1px solid #ddd;padding:6px;">${escapeHtml(m.category || "")}</td>
        <td style="border-bottom:1px solid #ddd;padding:6px;">${escapeHtml(m.name || "")}</td>
        <td style="border-bottom:1px solid #ddd;padding:6px;">${escapeHtml(m.level || "")}</td>
        <td style="border-bottom:1px solid #ddd;padding:6px;">${escapeHtml(m.description || "")}</td>
        <td style="border-bottom:1px solid #ddd;padding:6px;">${m.diploma ? "Ja" : "Nee"}</td>
      </tr>
    `).join("");

    const titleSuffix = isExternal ? "extern" : "intern";

    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(documentName || "Matching Personeel")} - ${titleSuffix}</title>
</head>
<body style="font-family:Arial,sans-serif;font-size:13px;color:#111;">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:20px;margin-bottom:18px;">
    <div>
      <h1 style="margin-bottom:4px;">Matching Personeel</h1>
      <div style="margin-bottom:18px;color:#555;">${escapeHtml(documentName || "")} ${isExternal ? "- extern" : "- intern"}</div>
    </div>
    <div>${photoBlock}</div>
  </div>

  <h2>Personeelskaart</h2>
  <table style="width:100%;border-collapse:collapse;">${tr}</table>

  <h2 style="margin-top:24px;">Matching gegevens</h2>
  <table style="width:100%;border-collapse:collapse;">
    <thead>
      <tr>
        <th style="text-align:left;border-bottom:2px solid #333;padding:6px;">Rubriek</th>
        <th style="text-align:left;border-bottom:2px solid #333;padding:6px;">Naam</th>
        <th style="text-align:left;border-bottom:2px solid #333;padding:6px;">Niveau</th>
        <th style="text-align:left;border-bottom:2px solid #333;padding:6px;">Omschrijving</th>
        <th style="text-align:left;border-bottom:2px solid #333;padding:6px;">Diploma</th>
      </tr>
    </thead>
    <tbody>${matching || `<tr><td colspan="5" style="padding:8px;">Geen matching gegevens.</td></tr>`}</tbody>
  </table>
</body>
</html>`;
  }

  function printPopup(printMode = "internal") {
    const html = buildPrintableHtml(printMode);
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) {
      window.alert("Popup kon niet worden geopend. Controleer popup-blokkering.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 250);
  }

  async function saveAsDocument() {
    if (!form.id) return;

    const name = safeTrim(documentName);
    if (!name) {
      setPopupError("Vul eerst een matchingnaam in.");
      return;
    }

    try {
      setPopupSaving(true);
      setPopupError("");

      const data = await tryFetchJson(
        [`/api/personnel/${form.id}/matching-document`, `/api/personeel/${form.id}/matching-document`],
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            matching_name: name,
            html: buildPrintableHtml("internal"),
          }),
        }
      );

      setFlash(`Document opgeslagen ✅ ${data?.file_name || ""}`);
      setTimeout(() => setFlash(""), 1800);
    } catch (err) {
      setPopupError(err?.message || String(err));
    } finally {
      setPopupSaving(false);
    }
  }

  function moveCursor(delta) {
    setListIndex((prev) => {
      const L = sortedItems.length;
      if (L <= 0) return -1;
      const start = prev < 0 ? 0 : prev;
      const next = Math.max(0, Math.min(L - 1, start + delta));
      setTimeout(() => rowRefs.current[next]?.scrollIntoView?.({ block: "nearest" }), 0);
      return next;
    });
  }

  function openCurrentRow() {
    const idx = listIndex < 0 ? 0 : listIndex;
    const row = sortedItems[idx];
    if (row) openPerson(row);
  }

  function focusNextCriteria(dir = +1) {
    const els = criteriaRefs.map((r) => r.current).filter(Boolean);
    if (!els.length) return;
    const active = document.activeElement;
    let idx = els.findIndex((el) => el === active);
    if (idx === -1) idx = dir > 0 ? -1 : els.length;
    let next = idx + dir;
    if (next < 0) next = 0;
    if (next > els.length - 1) next = els.length - 1;
    els[next]?.focus?.();
    els[next]?.scrollIntoView?.({ block: "nearest" });
  }

  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 6000,
    onCommand: (cmd) => {
      if (!cmd?.type) return false;
      if (cmd.type === "next") return focusNextCriteria(+1), true;
      if (cmd.type === "prev") return focusNextCriteria(-1), true;
      if (cmd.type === "refresh") return resetCriteria(), true;
      if (cmd.type === "select") return openCurrentRow(), true;
      if (cmd.type === "save") return savePerson(), true;
      return false;
    },
    onDictation: (text) => {
      const raw = String(text || "").trim();
      if (!raw) return;

      setSpeechInfo(`🎙 ${raw}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      const t = norm(raw);
      if (t === "volgende") return focusNextCriteria(+1);
      if (t === "vorige" || t === "terug") return focusNextCriteria(-1);
      if (t === "vernieuw" || t === "vernieuwen") return resetCriteria();
      if (t === "selecteer" || t === "item" || t === "open") return openCurrentRow();
      if (t === "print" || t === "printen") return popupOpen ? printPopup("internal") : undefined;
      if (t === "opslaan") return popupOpen ? savePerson() : undefined;
      if (t === "sluiten" || t === "klaar" || t === "stop") return popupOpen ? setPopupOpen(false) : undefined;
      if (t === "leeg") return resetCriteria();

      const active = document.activeElement;
      const entry = Object.entries(criteriaRefMap).find(([, ref]) => ref.current === active);

      if (entry) {
        const [key] = entry;
        setCrit(key, isEmptyWord(raw) ? "" : raw);
        return;
      }

      setCrit("wildcard", isEmptyWord(raw) ? "" : raw);
    },
  });

  const criteriaRefMap = {
    wildcard: wildcardRef,
    status: statusRef,
    functie: functieRef,
    uurloonMax: uurloonRef,
    regioBezoek: regioRef,
    woonplaatsBezoek: woonplaatsRef,
    rijbewijs: rijbewijsRef,
    eigenVervoer: eigenVervoerRef,
    opmerking: opmerkingRef,
    zzp: zzpRef,
    matchingGegevens: matchingRef,
    voornaam: voornaamRef,
    tussenvoegsel: tussenvoegselRef,
    achternaam: achternaamRef,
    geslacht: geslachtRef,
    geboortedatumMode: geboorteModeRef,
    geboortedatum: geboorteDatumRef,
  };

const isMobile = window.innerWidth <= 768;
  const RIGHT_WIDTH = isMobile ? "100%" : 320;
  const TABLE_MIN_WIDTH = 1250;

  const colWidths = {
    search: 120,
    status: 75,
    function: 95,
    pc: 70,
    city: 90,
    region: 70,
    phone: 80,
    mobile: 80,
    email: 105,
    zzp: 58,
    wage: 65,
    active: 55,
  };

  const pageStyle = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : `minmax(0, 1fr) minmax(${RIGHT_WIDTH}px, ${RIGHT_WIDTH}px)`,
    gap: isMobile ? 18 : 14,
    alignItems: "start",
    minWidth: 0,
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    color: "#222",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontSize: 13,
    fontFamily: "inherit",
  };

  const buttonDisabledStyle = {
    ...buttonStyle,
    opacity: 0.55,
    cursor: "not-allowed",
  };

  const popupPrintButtonStyle = {
    ...buttonStyle,
    background: "#6c757d",
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  const popupDocumentButtonStyle = {
    ...buttonStyle,
    background: "#198754",
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  const popupCloseButtonStyle = {
    ...buttonStyle,
    background: "#0d6efd",
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  const inputStyle = {
    width: "100%",
    padding: "7px 10px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
    fontSize: 13,
    fontFamily: "inherit",
    minWidth: 0,
  };

  const selectStyle = {
    display: "block",
    height: 30,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontSize: 13,
    color: "#333",
    fontWeight: 400,
    marginTop: 8,
    marginBottom: 3,
    display: "block",
  };

  const thStyle = {
    textAlign: "left",
    fontSize: 12,
    color: "#444",
    fontWeight: 700,
    padding: "8px 6px",
    borderBottom: "1px solid #e6e6e6",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "#fff",
    userSelect: "none",
  };

  const thClickable = { ...thStyle, cursor: "pointer" };

  const filterThStyle = {
    ...thStyle,
    fontWeight: 400,
    padding: "6px 6px 8px 6px",
    borderBottom: "1px solid #ededed",
    top: 32,
    zIndex: 4,
    cursor: "default",
  };

  const tdStyle = {
    borderBottom: "1px solid #efefef",
    padding: "7px 6px",
    fontSize: 13,
    color: "#222",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 180,
  };

  function filterSelect(value, setValue, options, includeYesNo = false) {
    return (
      <select value={value} onChange={(e) => setValue(e.target.value)} style={selectStyle}>
        <option value="">(alle)</option>
        {includeYesNo ? (
          <>
            <option value="j">j</option>
            <option value="n">n</option>
          </>
        ) : (
          options.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))
        )}
      </select>
    );
  }

  function renderCriteriaField(label, key, ref, type = "text") {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <input
          ref={ref}
          type={type}
          value={criteria[key] || ""}
          onChange={(e) => setCrit(key, e.target.value)}
          style={inputStyle}
        />
      </>
    );
  }

  function renderPopupText(label, key, type = "text") {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <input
          type={type}
          value={form[key] || ""}
          disabled={isReadonlyRole}
          onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
          style={inputStyle}
        />
      </>
    );
  }

  function renderPopupCommunicationText(label, key, type = "text", communicationType = "") {
    const value = String(form[key] || "").trim();

    function handleCommunicationClick(e) {
      if (!value) return;

      if (communicationType === "phone") {
        handlePhoneGridClick(e, value);
        return;
      }

      if (communicationType === "mobile") {
        handleMobileGridClick(e, value);
        return;
      }

      if (communicationType === "email") {
        handleEmailGridClick(e, value);
      }
    }

    return (
      <>
        <label style={labelStyle}>{label}</label>

        <div style={{ display: "grid", gridTemplateColumns: value ? "1fr auto" : "1fr", gap: 6 }}>
          <input
            type={type}
            value={form[key] || ""}
            disabled={isReadonlyRole}
            onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
            style={inputStyle}
          />

          {value && (
            <button
              type="button"
              onClick={handleCommunicationClick}
              style={{
                ...buttonStyle,
                padding: "7px 9px",
                whiteSpace: "nowrap",
              }}
              title={
                communicationType === "email"
                  ? `E-mailen: ${value}`
                  : communicationType === "mobile"
                    ? `Bellen / SMS / WhatsApp: ${value}`
                    : `Bellen: ${value}`
              }
            >
              {communicationType === "email" ? "✉️" : communicationType === "mobile" ? "📱" : "☎️"}
            </button>
          )}
        </div>
      </>
    );
  }

  function renderPopupCheck(label, key) {
    return (
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginTop: 9 }}>
        <input
          type="checkbox"
          checked={form[key] === true}
          disabled={isReadonlyRole}
          onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.checked }))}
        />
        {label}
      </label>
    );
  }

  return (
    <div>
      <div style={pageStyle}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ marginTop: 0, marginBottom: 10 }}>
            Matching Personeel <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
          </h1>
{!isMobile ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap" }}>
              <input
                ref={searchRef}
                value={criteria.wildcard}
                onChange={(e) => setCrit("wildcard", e.target.value)}
                placeholder="Wildcard zoeken..."
                style={{ ...inputStyle, maxWidth: 420 }}
              />

              <div
                title={`Aantal regels in het grid: ${gridCount}`}
                style={{
                  minWidth: 52,
                  height: 32,
                  padding: "0 10px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                  background: "#f7fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#1f4e79",
                  boxSizing: "border-box",
                  whiteSpace: "nowrap",
                }}
              >
                {gridCount}
              </div>

              <button
                onClick={() => voice.startOnce?.()}
                disabled={!voice.supported || voice.listening}
                style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
              >
                {voice.listening ? "🎤 Luistert..." : "🎤 Spreek"}
              </button>

              <button onClick={resetCriteria} style={buttonStyle}>🔄 Vernieuw</button>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) 58px",
                  gap: 6,
                  marginBottom: 8,
                  alignItems: "center",
                }}
              >
                <input
                  ref={searchRef}
                  value={criteria.wildcard}
                  onChange={(e) => setCrit("wildcard", e.target.value)}
                  placeholder="Wildcard zoeken..."
                  style={{
                    ...inputStyle,
                    width: "100%",
                    minWidth: 0,
                  }}
                />

                <div
                  title={`Aantal regels in het grid: ${gridCount}`}
                  style={{
                    height: 34,
                    minHeight: 34,
                    padding: "0 8px",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    background: "#f7fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#1f4e79",
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                    lineHeight: "1",
                  }}
                >
                  {gridCount}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 8 }}>
                <button
                  onClick={() => voice.startOnce?.()}
                  disabled={!voice.supported || voice.listening}
                  style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
                >
                  {voice.listening ? "🎤..." : "🎤 Spreek"}
                </button>
                <button onClick={resetCriteria} style={buttonStyle}>🔄 Vernieuw</button>
              </div>
            </>
          )}

          <div style={{ marginTop: 6, fontSize: 13 }}>
            {loading && <span>Laden...</span>}
            {error && <span style={{ color: "crimson" }}>{error}</span>}
            {popupError && <span style={{ color: "crimson" }}>{popupError}</span>}
            {flash && <span style={{ color: "#0b7a3b" }}>{flash}</span>}
            {speechInfo && <span style={{ marginLeft: 8, opacity: 0.75 }}>{speechInfo}</span>}
          </div>

          <div style={{ marginTop: 8, background: "white", borderRadius: 6, border: "1px solid #f0f0f0", overflow: "hidden" }}>
            <div style={{ overflow: "auto", maxHeight: isMobile ? "none" : "calc(100vh - 190px)" }}>
              <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%", minWidth: TABLE_MIN_WIDTH, tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: colWidths.search }} />
                  <col style={{ width: colWidths.status }} />
                  <col style={{ width: colWidths.function }} />
                  <col style={{ width: colWidths.pc }} />
                  <col style={{ width: colWidths.city }} />
                  <col style={{ width: colWidths.region }} />
                  <col style={{ width: colWidths.phone }} />
                  <col style={{ width: colWidths.mobile }} />
                  <col style={{ width: colWidths.email }} />
                  <col style={{ width: colWidths.zzp }} />
                  <col style={{ width: colWidths.wage }} />
                  <col style={{ width: colWidths.active }} />
                </colgroup>

                <thead>
                  <tr>
                    <th style={thClickable} onClick={() => sortToggle("search")}>Zoeknaam{sortArrow("search")}</th>
                    <th style={thClickable} onClick={() => sortToggle("status")}>Status{sortArrow("status")}</th>
                    <th style={thClickable} onClick={() => sortToggle("function")}>Functie{sortArrow("function")}</th>
                    <th style={thClickable} onClick={() => sortToggle("pc")}>Postcode{sortArrow("pc")}</th>
                    <th style={thClickable} onClick={() => sortToggle("city")}>Plaats{sortArrow("city")}</th>
                    <th style={thClickable} onClick={() => sortToggle("region")}>Regio{sortArrow("region")}</th>
                    <th style={thClickable} onClick={() => sortToggle("phone")}>Telefoon{sortArrow("phone")}</th>
                    <th style={thClickable} onClick={() => sortToggle("mobile")}>Mobiel{sortArrow("mobile")}</th>
                    <th style={thClickable} onClick={() => sortToggle("email")}>E-mail{sortArrow("email")}</th>
                    <th style={thClickable} onClick={() => sortToggle("zzp")}>ZZP{sortArrow("zzp")}</th>
                    <th style={thClickable} onClick={() => sortToggle("wage")}>Uurloon{sortArrow("wage")}</th>
                    <th style={thClickable} onClick={() => sortToggle("active")}>Actief{sortArrow("active")}</th>
                  </tr>

                  <tr>
                    <th style={filterThStyle}>{filterSelect(searchNameFilter, setSearchNameFilter, searchNameOptions)}</th>
                    <th style={filterThStyle}>{filterSelect(statusFilter, setStatusFilter, statusOptions)}</th>
                    <th style={filterThStyle}>{filterSelect(functionFilter, setFunctionFilter, functionOptions)}</th>
                    <th style={filterThStyle}>{filterSelect(pcFilter, setPcFilter, pcOptions)}</th>
                    <th style={filterThStyle}>{filterSelect(cityFilter, setCityFilter, cityOptions)}</th>
                    <th style={filterThStyle}>{filterSelect(regionFilter, setRegionFilter, regionOptions)}</th>
                    <th style={filterThStyle}>{filterSelect(phoneFilter, setPhoneFilter, phoneOptions)}</th>
                    <th style={filterThStyle}>{filterSelect(mobileFilter, setMobileFilter, mobileOptions)}</th>
                    <th style={filterThStyle}>{filterSelect(emailFilter, setEmailFilter, emailOptions)}</th>
                    <th style={filterThStyle}>{filterSelect(zzpFilter, setZzpFilter, [], true)}</th>
                    <th style={filterThStyle}>{filterSelect(wageFilter, setWageFilter, wageOptions)}</th>
                    <th style={filterThStyle}>{filterSelect(activeFilter, setActiveFilter, [], true)}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((it, idx) => {
                    const selected = idx === listIndex;
                    return (
                      <tr
                        key={it.id}
                        ref={(el) => (rowRefs.current[idx] = el)}
                        onClick={() => {
                          setListIndex(idx);
                          openPerson(it);
                        }}
                        style={{
                          background: selected ? "#e6f2fb" : idx % 2 ? "#fbfdff" : "white",
                          cursor: "pointer",
                        }}
                      >
                        <td style={tdStyle} title={it.search_name || ""}>{it.search_name || "-"}</td>
                        <td style={tdStyle}>{it.status || "-"}</td>
                        <td style={tdStyle} title={it.job_functions || ""}>{it.job_functions || "-"}</td>
                        <td style={tdStyle}>{it.corr_postcode || it.visit_postcode || "-"}</td>
                        <td style={tdStyle}>{it.corr_city || it.visit_city || "-"}</td>
                        <td style={tdStyle}>{it.corr_region || it.visit_region || "-"}</td>
                        <td style={tdStyle} title={it.phone || ""}>
                          {it.phone ? (
                            <button
                              type="button"
                              onClick={(e) => handlePhoneGridClick(e, it.phone)}
                              style={{
                                border: "none",
                                background: "transparent",
                                padding: 0,
                                margin: 0,
                                color: "#1f4e79",
                                textDecoration: "underline",
                                cursor: "pointer",
                                font: "inherit",
                              }}
                              title={`Bellen: ${it.phone}`}
                            >
                              {it.phone}
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td style={tdStyle} title={it.mobile || ""}>
                          {it.mobile ? (
                            <button
                              type="button"
                              onClick={(e) => handleMobileGridClick(e, it.mobile)}
                              style={{
                                border: "none",
                                background: "transparent",
                                padding: 0,
                                margin: 0,
                                color: "#1f4e79",
                                textDecoration: "underline",
                                cursor: "pointer",
                                font: "inherit",
                              }}
                              title={`Bellen / SMS / WhatsApp: ${it.mobile}`}
                            >
                              {it.mobile}
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td style={tdStyle} title={it.email || ""}>
                          {it.email ? (
                            <button
                              type="button"
                              onClick={(e) => handleEmailGridClick(e, it.email)}
                              style={{
                                border: "none",
                                background: "transparent",
                                padding: 0,
                                margin: 0,
                                color: "#1f4e79",
                                textDecoration: "underline",
                                cursor: "pointer",
                                font: "inherit",
                              }}
                              title={`E-mailen: ${it.email}`}
                            >
                              {it.email}
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td style={tdStyle}>{it.zzp ? "j" : "n"}</td>
                        <td style={tdStyle}>{it.hourly_wage ? formatMoney(it.hourly_wage) : "-"}</td>
                        <td style={tdStyle}>{parseActiveValue(it) === false ? "n" : "j"}</td>
                      </tr>
                    );
                  })}

                  {sortedItems.length === 0 && (
                    <tr>
                      <td colSpan={12} style={{ padding: 12, opacity: 0.7 }}>
                        Geen resultaten.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div
          style={{
            width: isMobile ? "100%" : RIGHT_WIDTH,
            minWidth: isMobile ? 0 : RIGHT_WIDTH,
            maxWidth: isMobile ? "100%" : RIGHT_WIDTH,
            position: isMobile ? "relative" : "sticky",
            top: isMobile ? "auto" : 0,
            alignSelf: "start",
            maxHeight: isMobile ? "none" : "calc(100vh - 40px)",
            overflowY: isMobile ? "visible" : "auto",
            background: "linear-gradient(180deg, #0b4f7a 0%, #083b5c 100%)",
            borderRadius: 10,
            padding: 10,
            boxSizing: "border-box",
            marginTop: isMobile ? 6 : 0,
            paddingTop: isMobile ? 6 : 10,
          }}
        >
          <div style={{ background: "white", borderRadius: 10, padding: 12, boxSizing: "border-box", width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>Selectiecriteria</h3>

            {renderCriteriaField("Wildcard", "wildcard", wildcardRef)}
            {renderCriteriaField("Status", "status", statusRef)}
            {renderCriteriaField("Functie", "functie", functieRef)}
            {renderCriteriaField("Uurloon max", "uurloonMax", uurloonRef)}
            {renderCriteriaField("Regio bezoek", "regioBezoek", regioRef)}
            {renderCriteriaField("Woonplaats bezoek", "woonplaatsBezoek", woonplaatsRef)}
            {renderCriteriaField("Rijbewijs", "rijbewijs", rijbewijsRef)}

            <label style={labelStyle}>Eigen vervoer</label>
            <select ref={eigenVervoerRef} value={criteria.eigenVervoer} onChange={(e) => setCrit("eigenVervoer", e.target.value)} style={inputStyle}>
              <option value="">Alle</option>
              <option value="j">Ja</option>
              <option value="n">Nee</option>
            </select>

            {renderCriteriaField("Opmerking", "opmerking", opmerkingRef)}

            <label style={labelStyle}>ZZP j/n</label>
            <select ref={zzpRef} value={criteria.zzp} onChange={(e) => setCrit("zzp", e.target.value)} style={inputStyle}>
              <option value="">Alle</option>
              <option value="j">Ja</option>
              <option value="n">Nee</option>
            </select>

            {renderCriteriaField("Matching gegevens", "matchingGegevens", matchingRef)}
            {renderCriteriaField("Voornaam", "voornaam", voornaamRef)}
            {renderCriteriaField("Tussenvoegsel", "tussenvoegsel", tussenvoegselRef)}
            {renderCriteriaField("Achternaam", "achternaam", achternaamRef)}
            {renderCriteriaField("Geslacht", "geslacht", geslachtRef)}

            <label style={labelStyle}>Geboortedatum</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              <select ref={geboorteModeRef} value={criteria.geboortedatumMode} onChange={(e) => setCrit("geboortedatumMode", e.target.value)} style={inputStyle}>
                <option value="">Geen</option>
                <option value="jonger">Jonger dan</option>
                <option value="ouder">Ouder dan</option>
              </select>
              <input ref={geboorteDatumRef} type="date" value={criteria.geboortedatum} onChange={(e) => setCrit("geboortedatum", e.target.value)} style={inputStyle} />
            </div>

          </div>
        </div>
      </div>

      {popupOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? 8 : 20,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 12,
              width: "min(1180px, 100%)",
              maxHeight: "94dvh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: 12,
                borderBottom: "1px solid #d9e1e7",
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div>
                <b>Personeelskaart</b> <span style={{ opacity: 0.65 }}>{form.search_name || form.id}</span>
              </div>

              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button onClick={() => printPopup("internal")} style={popupPrintButtonStyle}>🖨 Printen intern</button>
                <button onClick={() => printPopup("external")} style={popupPrintButtonStyle}>🖨 Printen extern</button>
                <button onClick={saveAsDocument} disabled={popupSaving} style={popupSaving ? buttonDisabledStyle : popupDocumentButtonStyle}>🗂 Opslaan als document</button>
                <button onClick={() => setPopupOpen(false)} style={popupCloseButtonStyle}>Sluiten</button>
              </div>
            </div>

            <div ref={popupScrollRef} style={{ padding: 12, overflowY: "auto" }}>
              {popupError && <div style={{ color: "crimson", marginBottom: 8 }}>{popupError}</div>}
              {flash && <div style={{ color: "#0b7a3b", marginBottom: 8 }}>{flash}</div>}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "0.95fr 0.95fr 0.95fr 170px",
                  gap: 10,
                  alignItems: "start",
                }}
              >
                <div>
                  <h3>Naam / privé</h3>
                  {renderPopupText("Voornaam", "first_name")}
                  {renderPopupText("Voorletters", "initials")}
                  {renderPopupText("Tussenvoegsel", "name_prefix")}
                  {renderPopupText("Achternaam", "last_name")}
                  {renderPopupText("Zoeknaam", "search_name")}
                  {renderPopupText("Geslacht", "gender")}
                  {renderPopupText("Geboortedatum", "birth_date", "date")}
                </div>

                <div>
                  <h3>HRM</h3>
                  {renderPopupText("Status", "status")}
                  {renderPopupText("Functie", "job_functions")}
                  {renderPopupText("Uurloon", "hourly_wage")}
                  {renderPopupText("Verkooptarief", "sales_rate")}
                  {renderPopupText("Rijbewijs", "driving_license")}
                  {renderPopupCheck("Eigen vervoer", "own_transport")}
                  {renderPopupCheck("ZZP", "zzp")}
                  {renderPopupCheck("Actief", "active")}
                </div>

                <div>
                  <h3>Bezoekadres</h3>
                  {renderPopupText("Straat", "visit_street")}
                  {renderPopupText("Huisnummer", "visit_house_number")}
                  {renderPopupText("Toevoeging", "visit_addition")}
                  {renderPopupText("Postcode", "visit_postcode")}
                  {renderPopupText("Plaats", "visit_city")}
                  {renderPopupText("Provincie", "visit_province")}
                  {renderPopupText("Regio", "visit_region")}
                  {renderPopupText("Land", "visit_country")}
                </div>

                <div>
                  <div style={{ display: "flex", justifyContent: isMobile ? "flex-start" : "center", marginBottom: 8 }}>
                    {form.id && form.profile_photo_path ? (
                      <img
                        src={photoUrl(form.id)}
                        alt="Pasfoto"
                        style={{
                          width: 128,
                          height: 166,
                          objectFit: "cover",
                          display: "block",
                          border: "1px solid #d9d9d9",
                          borderRadius: 8,
                          background: "#f7f7f7",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 128,
                          height: 166,
                          border: "1px solid #d9d9d9",
                          borderRadius: 8,
                          background: "#f7f7f7",
                        }}
                      />
                    )}
                  </div>

                  {renderPopupCommunicationText("Telefoon", "phone", "text", "phone")}
                  {renderPopupCommunicationText("Mobiel", "mobile", "text", "mobile")}
                  {renderPopupCommunicationText("E-mail", "email", "email", "email")}
                  {renderPopupText("Website", "website")}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                  gap: 12,
                  marginTop: 12,
                  alignItems: "start",
                }}
              >
                <div>
                  <h3>ID documenten</h3>
                  {renderPopupText("ID soort", "id_type")}
                  {renderPopupText("ID nummer", "id_number")}
                  {renderPopupText("ID geldig tot", "id_expiry_date", "date")}
                  {renderPopupText("BSN", "bsn")}
                  {renderPopupText("Verblijfsvergunning", "residence_permit")}
                </div>

                <div>
                  <h3>Opmerking</h3>
                  <textarea
                    value={form.remarks || ""}
                    disabled={isReadonlyRole}
                    onChange={(e) => setForm((prev) => ({ ...prev, remarks: e.target.value }))}
                    style={{ ...inputStyle, minHeight: 142, resize: "vertical" }}
                  />
                </div>

                <div>
                  <h3>Document opslaan</h3>
                  <label style={labelStyle}>Zelf toe te kennen matchingnaam</label>
                  <input value={documentName} onChange={(e) => setDocumentName(e.target.value)} style={inputStyle} />
                  <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
                    Wordt opgeslagen onder: Personeel / [nr - zoeknaam] / Matching / [matchingnaam].html
                  </div>
                </div>
              </div>

              <h3 style={{ marginTop: 16 }}>Matching gegevens</h3>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Rubriek</th>
                      <th style={thStyle}>Naam</th>
                      <th style={thStyle}>Niveau</th>
                      <th style={thStyle}>Omschrijving</th>
                      <th style={thStyle}>Van</th>
                      <th style={thStyle}>Tot</th>
                      <th style={thStyle}>Diploma</th>
                      <th style={thStyle}>Werkgever</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(matchingRows || []).map((m) => (
                      <tr key={m.id || `${m.category}-${m.name}`}>
                        <td style={tdStyle}>{m.category || "-"}</td>
                        <td style={tdStyle}>{m.name || "-"}</td>
                        <td style={tdStyle}>{m.level || "-"}</td>
                        <td style={tdStyle} title={m.description || ""}>{m.description || "-"}</td>
                        <td style={tdStyle}>{formatDateNL(m.date_from) || "-"}</td>
                        <td style={tdStyle}>{formatDateNL(m.date_to) || "-"}</td>
                        <td style={tdStyle}>{m.diploma ? "j" : "n"}</td>
                        <td style={tdStyle}>{m.employer || "-"}</td>
                      </tr>
                    ))}
                    {matchingRows.length === 0 && (
                      <tr><td colSpan={8} style={{ padding: 10, opacity: 0.7 }}>Geen matching gegevens.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, flexWrap: "wrap", marginTop: 14 }}>
                <button onClick={() => printPopup("internal")} style={popupPrintButtonStyle}>🖨 Printen intern</button>
                <button onClick={() => printPopup("external")} style={popupPrintButtonStyle}>🖨 Printen extern</button>
                <button onClick={saveAsDocument} disabled={popupSaving} style={popupSaving ? buttonDisabledStyle : popupDocumentButtonStyle}>🗂 Opslaan als document</button>
                <button onClick={() => setPopupOpen(false)} style={popupCloseButtonStyle}>Sluiten</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
